import { useUpdateSettingModuleStatusMutation } from "entities/brands/settings/mutations/useUpdateSettingModuleStatusMutation";
import { useModuleListByBrandSettingQuery } from "entities/brands/settings/queries/useModuleListByBrandSettingQuery";
import { useSettingsListByBrandQuery } from "entities/brands/settings/queries/useSettingsListByBrandQuery";
import { PageModuleData, PageModuleStatus } from "entities/pages/types";
import { FC, PropsWithChildren } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useDynamicRouting } from "shared/hooks/useDynamicRouting";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import { BRAND_TAB_PATH } from "shared/routes/paths";
import { createContext } from "shared/utils/context";
import { SettingsModulesType } from "./types";

const [Provider, useSettingsModules] = createContext<SettingsModulesType>({
  name: "SettingsModulesContext",
  hookName: "useSettingsModules",
  providerName: "SettingsModulesProvider",
});

const SettingsModulesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { id, tabDetailsId: tabDetails } = useParams();
  const { pathname } = useLocation();
  const moduleDetailsDrawer = useDrawerState<PageModuleData>();

  const settingId = String(tabDetails);
  const brandId = String(id);

  const { data, isLoading } = useModuleListByBrandSettingQuery(
    brandId,
    settingId,
  );

  const { dynamicRoutes } = useDynamicRouting();

  const { mutate: updateModuleStatus } = useUpdateSettingModuleStatusMutation({
    brandId,
    settingId,
  });

  const onUpdateStatus = async ({
    status,
    moduleId,
  }: {
    status: PageModuleStatus;
    moduleId: string;
  }) => {
    updateModuleStatus({ status, moduleId });
  };

  const { data: settings } = useSettingsListByBrandQuery(brandId);

  const searchData = useLocalSearch({
    searchableFields: ["name"],
    initialData: data || [],
  });

  const groupPageTitle = dynamicRoutes.reduce((acc, next) => {
    if (next.pathname === BRAND_TAB_PATH) {
      const groupTitle = next.title;
      const subGroupTitle =
        next.elements?.find((element) => pathname.includes(element.pathname))
          ?.title ?? "";

      return `${groupTitle} \u2022 ${subGroupTitle}`;
    }

    return acc;
  }, "");

  const modulePageTitle =
    settings?.find((module) => module.id === settingId)?.title ?? "";

  const value = {
    data: data || [],
    isLoadingPages: isLoading,
    modulePageTitle,
    groupPageTitle,
    moduleDetailsDrawer,
    onUpdateStatus,
    brandId,
    settingId: String(settingId),
    searchData: {
      value: searchData.value,
      onChange: searchData.onChange,
      filteredData: searchData.filteredData,
      isSearchActive: Boolean(searchData.value),
    },
  };

  return <Provider value={value}>{children}</Provider>;
};

export { SettingsModulesProvider, useSettingsModules };
